import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  Alert,
  AlertTitle,
  CircularProgress,
  IconButton,
  Tooltip,
  Button,
  TextField
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SearchIcon from "@mui/icons-material/Search";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { GET } from "APIclient";
import properties from "constants/ApplicationConstants";

export default function AppraisalViewRating() {
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [ratingsData, setRatingsData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [empId, setEmpId] = useState(null);
  const [notification, setNotification] = useState({ open: false, message: "", type: "" });

  useEffect(() => {
    const storedEmpId = localStorage.getItem("empId") || sessionStorage.getItem("empId");
    if (storedEmpId) {
      const parsedEmpId = parseInt(storedEmpId);
      setEmpId(parsedEmpId);
      fetchRatingsData(parsedEmpId);
    } else {
      showNotification("Employee ID not found", "error");
      setLoading(false);
    }
  }, []);

  const fetchRatingsData = async employeeId => {
    try {
      setLoading(true);
      const response = await GET(`manager/employee/${employeeId}/view`);

      console.log("API Response:", response);

      if (response.data && response.data.data) {
        const data = Array.isArray(response.data.data) ? response.data.data : [response.data.data];

        const transformedData = data.map((item, index) => ({
          id: item.appraisalId || index,
          appraisalId: item.appraisalId,
          empId: item.empId,
          employeeStartDate: item.employeeStartDate || "N/A",
          employeeEndDate: item.employeeEndDate || "N/A",
          appraisalPeriod: item.appraisalPeriod || item.date || "N/A"
        }));

        setRatingsData(transformedData);
        setFilteredData(transformedData);
      } else {
        showNotification("No ratings data found", "info");
        setRatingsData([]);
        setFilteredData([]);
      }
    } catch (error) {
      console.error("Error fetching ratings data:", error);
      showNotification(error.response?.data?.message || "Failed to load ratings data", "error");
      setRatingsData([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  };

  // Normalize various date formats to a comparable Date object
  const parseDate = (dateStr) => {
    if (!dateStr || dateStr === "N/A") return null;
    // Handles: "DD/MM/YYYY", "YYYY-MM-DD", "MM-DD-YYYY", ISO strings, etc.
    const parts = dateStr.split(/[-\/]/);
    if (parts.length === 3) {
      // If first part is 4 digits → YYYY-MM-DD
      if (parts[0].length === 4) return new Date(`${parts[0]}-${parts[1]}-${parts[2]}`);
      // If last part is 4 digits → DD/MM/YYYY or MM/DD/YYYY (assume DD/MM/YYYY)
      if (parts[2].length === 4) return new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
    }
    return new Date(dateStr); // fallback: native parsing
  };

  const handleSearch = () => {
    if (!startDate && !endDate) {
      setFilteredData(ratingsData);
      showNotification("Showing all records", "info");
      return;
    }

    const filterStart = startDate ? new Date(startDate) : null;
    const filterEnd = endDate ? new Date(endDate) : null;

    const filtered = ratingsData.filter(item => {
      const itemStart = parseDate(item.employeeStartDate);
      const itemEnd = parseDate(item.employeeEndDate);

      // Both filter dates provided: KRA cycle must overlap or fall within range
      if (filterStart && filterEnd) {
        if (!itemStart || !itemEnd) return false;
        return itemStart >= filterStart && itemEnd <= filterEnd;
      }
      // Only start date provided
      if (filterStart) {
        if (!itemStart) return false;
        return itemStart >= filterStart;
      }
      // Only end date provided
      if (filterEnd) {
        if (!itemEnd) return false;
        return itemEnd <= filterEnd;
      }
      return true;
    });

    setFilteredData(filtered);

    if (filtered.length === 0) {
      showNotification("No records found for the selected date range", "warning");
    } else {
      showNotification(`Found ${filtered.length} record(s)`, "success");
    }
  };

  const handleClearFilter = () => {
    setStartDate("");
    setEndDate("");
    setFilteredData(ratingsData);
    showNotification("Filter cleared", "info");
  };

  const handleViewDetails = (appraisalId, employeeId) => {
    navigate("/appraisal", {
      state: {
        appraisalId: appraisalId,
        empId: employeeId,
        viewMode: true
      }
    });
  };

  const showNotification = (message, type) => {
    setNotification({ open: true, message, type });
    setTimeout(() => {
      setNotification({ open: false, message: "", type: "" });
    }, 4000);
  };

  const columns = [
    {
      field: "appraisalPeriod",
      headerName: "Appraisal Period",
      width: 200,
      filterable: true
    },
    {
      field: "employeeStartDate",
      headerName: "KRA Cycle Start Date",
      width: 200,
      filterable: true
    },
    {
      field: "employeeEndDate",
      headerName: "KRA Cycle End Date",
      width: 200,
      filterable: true
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      align: "center",
      headerAlign: "center",
      sortable: false,
      filterable: false,
      renderCell: params => (
        <Tooltip title="View Details">
          <IconButton
            color="info"
            size="small"
            onClick={() => handleViewDetails(params.row.appraisalId, params.row.empId)}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )
    }
  ];

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox
          pt={2}
          pb={2}
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="40vh"
        >
          <CircularProgress />
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={0.5} pb={1.5}>
        <Card>
          <MDBox
            variant="gradient"
            sx={{ background: properties.colors.bgcolorbluethem }}
            bgColor="info"
            borderRadius="lg"
            coloredShadow="success"
            mx={1.5}
            mt={1}
            p={1.5}
            mb={0}
            textAlign="center"
          >
            <MDTypography
              variant="h5"
              sx={{ fontFamily: properties.fontFamily }}
              fontWeight="400"
              color="white"
            >
              View Previous Appraisal Form
            </MDTypography>
          </MDBox>

          <MDBox p={1.5}>
            {ratingsData.length > 0 ? (
              <>
                {/* ── Filter Bar ── */}
                <MDBox
                  display="flex"
                  justifyContent="flex-start"
                  alignItems="center"
                  mb={2}
                  gap={2}
                  flexWrap="wrap"
                >
                  <TextField
                    label="KRA Cycle Start Date"
                    type="date"
                    size="small"
                    value={startDate}
                    onChange={e => setStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: 200 }}
                  />
                  <TextField
                    label="KRA Cycle End Date"
                    type="date"
                    size="small"
                    value={endDate}
                    onChange={e => setEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: startDate || undefined }}
                    sx={{ minWidth: 200 }}
                  />
                  <Button
                    variant="contained"
                    startIcon={<SearchIcon />}
                    onClick={handleSearch}
                    size="small"
                    sx={{
                      background: properties.colors.bgcolorbluethem,
                      fontFamily: properties.fontFamily,
                      color: "#fff",
                      height: 40
                    }}
                  >
                    SEARCH
                  </Button>
                  {(startDate || endDate) && (
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={handleClearFilter}
                      size="small"
                      sx={{ height: 40, fontFamily: properties.fontFamily }}
                    >
                      CLEAR
                    </Button>
                  )}
                </MDBox>

                {/* ── Data Grid ── */}
                <Box sx={{ height: 450, width: "100%" }}>
                  <DataGrid
                    rows={filteredData}
                    columns={columns}
                    pageSize={10}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    checkboxSelection={false}
                    disableSelectionOnClick
                    components={{ Toolbar: GridToolbar }}
                    componentsProps={{
                      toolbar: {
                        showQuickFilter: true,
                        quickFilterProps: { debounceMs: 500 }
                      }
                    }}
                    sx={{
                      "& .MuiDataGrid-row:hover": { backgroundColor: "#f5f5f5" },
                      "& .MuiDataGrid-columnHeaders": {
                        backgroundColor: "#f0f0f0",
                        fontWeight: "600"
                      }
                    }}
                  />
                </Box>
              </>
            ) : (
              <Alert severity="info" sx={{ fontFamily: properties.fontFamily }}>
                <AlertTitle variant="h6" sx={{ fontFamily: properties.fontFamily }}>
                  No Ratings Data Found
                </AlertTitle>
                No ratings data available for this employee.
              </Alert>
            )}
          </MDBox>
        </Card>

        {notification.open && (
          <MDBox
            sx={{ position: "fixed", top: 20, right: 20, zIndex: 9999, minWidth: "300px" }}
          >
            <Alert
              severity={notification.type}
              onClose={() => setNotification({ open: false, message: "", type: "" })}
            >
              {notification.message}
            </Alert>
          </MDBox>
        )}
      </MDBox>
    </DashboardLayout>
  );
}